import { Option, TableRow } from '@code/innovance-react-ui-kit';
import React, { createContext, FC, useContext, useReducer } from 'react';

import {
  ClientInfoNameResponse,
  ContactInfoChangeResponse,
  DocumentUploadClientInfoResponse,
} from 'src/front/api/generated';
import { UploadDocumentFormData } from 'src/front/components/document/UploadDocumentModal';

export enum ACTIONS {
  SEARCH_CUSTOMER,
  SET_DOCUMENTS,
  SET_CLIENT_INFO,
  SET_CLIENT_NAME,
  SET_CONTACT_PHONE_NUMBER,
  CLEAR,
}

type Action =
  | {
      type: ACTIONS.SEARCH_CUSTOMER;
      payload: {
        customerSearchValues?: CustomerSearchValues;
        cardPageNumber?: number;
      };
    }
  | { type: ACTIONS.SET_DOCUMENTS; payload: { documents?: UploadDocumentFormData[] } }
  | { type: ACTIONS.SET_CLIENT_INFO; payload: { clientInfo?: DocumentUploadClientInfoResponse } }
  | { type: ACTIONS.SET_CLIENT_NAME; payload: { clientName?: ClientInfoNameResponse } }
  | {
      type: ACTIONS.SET_CONTACT_PHONE_NUMBER;
      payload: { contactPhoneNumber?: ContactInfoChangeResponse };
    }
  | { type: ACTIONS.CLEAR };

interface CustomerSearchValues {
  customerSurname: string;
  customerName: string;
  customerSecondName: string;
  birthDate: Date | null;
  selectedCustomer?: TableRow;

  identityDocumentSerialCode: string;
  identityDocumentSerialNumber: string;
}

interface ContactInfoState {
  customerId?: string;
  customerNumber?: string;
  customerSearchValues?: CustomerSearchValues;
  pageNumber?: number;
  documentTypeValue?: Option | null;
  documents?: UploadDocumentFormData[];
  clientInfo?: DocumentUploadClientInfoResponse;
  contactPhoneNumber?: ContactInfoChangeResponse;
  clientName?: ClientInfoNameResponse;
}

const initialValues = {};

type ContactInfoDispatch = (action: Action) => void;

const ContactInfoStateContext = createContext<ContactInfoState | undefined>(undefined);
const ContactInfoDispatchContext = createContext<ContactInfoDispatch | undefined>(undefined);

const contactInfoReducer = (state: ContactInfoState, action: Action): ContactInfoState => {
  switch (action.type) {
    case ACTIONS.SEARCH_CUSTOMER:
    case ACTIONS.SET_DOCUMENTS:
    case ACTIONS.SET_CLIENT_INFO:
    case ACTIONS.SET_CLIENT_NAME:
    case ACTIONS.SET_CONTACT_PHONE_NUMBER:
      return {
        ...state,
        ...action.payload,
      };
    case ACTIONS.CLEAR:
      return initialValues;
    default:
      return state;
  }
};

const ContactInfoProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(contactInfoReducer, initialValues);

  return (
    <ContactInfoStateContext.Provider value={state}>
      <ContactInfoDispatchContext.Provider value={dispatch}>
        {children}
      </ContactInfoDispatchContext.Provider>
    </ContactInfoStateContext.Provider>
  );
};

export const useCustomerSearchState = (): ContactInfoState => {
  const context = useContext(ContactInfoStateContext);
  if (context === undefined) {
    throw new Error('useCustomerSearchState must be used within a ContactInfoProvider');
  }

  return context;
};

type SearchInput = Pick<
  ContactInfoState,
  'customerId' | 'customerNumber' | 'customerSearchValues' | 'pageNumber'
>;

type Documents = Pick<ContactInfoState, 'documents'>;
type ClientInfo = Pick<ContactInfoState, 'clientInfo'>;
type ContactPhoneNumber = Pick<ContactInfoState, 'contactPhoneNumber'>;
type ClientName = Pick<ContactInfoState, 'clientName'>;

export const useCustomerSearchDispatch = (): ((payload: SearchInput) => void) => {
  const dispatch = useContext(ContactInfoDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useCustomerSearchDispatch must be used within a ContactInfoProvider');
  }

  return (payload: SearchInput) => dispatch({ type: ACTIONS.SEARCH_CUSTOMER, payload });
};

export const useSetDocumentsDispatch = (): ((payload: Documents) => void) => {
  const dispatch = useContext(ContactInfoDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetDocumentsDispatch must be used within a ContactInfoProvider');
  }

  return (payload: Documents) => dispatch({ type: ACTIONS.SET_DOCUMENTS, payload });
};

export const useSetClientInfoDispatch = (): ((payload: ClientInfo) => void) => {
  const dispatch = useContext(ContactInfoDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetClientInfoDispatch must be used within a ContactInfoProvider');
  }

  return (payload: ClientInfo) => dispatch({ type: ACTIONS.SET_CLIENT_INFO, payload });
};

export const useSetContactPhoneNumberDispatch = (): ((payload: ContactPhoneNumber) => void) => {
  const dispatch = useContext(ContactInfoDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetClientNameDispatch must be used within a ContactInfoProvider');
  }

  return (payload: ContactPhoneNumber) =>
    dispatch({ type: ACTIONS.SET_CONTACT_PHONE_NUMBER, payload });
};
export const useSetClientNameDispatch = (): ((payload: ClientName) => void) => {
  const dispatch = useContext(ContactInfoDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetClientNameDispatch must be used within a ContactInfoProvider');
  }
  return (payload: ClientName) => dispatch({ type: ACTIONS.SET_CLIENT_NAME, payload });
};
export const useClearDispatch = (): (() => void) => {
  const dispatch = useContext(ContactInfoDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClearDispatch must be used within a ContactInfoProvider');
  }

  return () => dispatch({ type: ACTIONS.CLEAR });
};

export default ContactInfoProvider;
